import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { createMaintenance, resetCreateStatus, resetUpdateStatus, selectCreateMaintenanceError, selectCreateMaintenanceLoading, selectSelectedMaintenance, selectSelectedMaintenanceLoading, selectUpdateMaintenanceError, selectUpdateMaintenanceLoading, updateMaintenance } from "../../redux/maintenances/maintenancesSlice";
import { closeModal, setHasToRefresh } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faPlus, faTimes, faEdit } from '@fortawesome/free-solid-svg-icons';

export default function AddEditMaintenance({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const selectedMaintenance = useSelector(selectSelectedMaintenance);
    const selectedMaintenanceLoading = useSelector(selectSelectedMaintenanceLoading);
    const createLoading = useSelector(selectCreateMaintenanceLoading);
    const createError = useSelector(selectCreateMaintenanceError);
    const updateLoading = useSelector(selectUpdateMaintenanceLoading);
    const updateError = useSelector(selectUpdateMaintenanceError);
    
    // Funzione per ottenere il colore dello stato delle attività di manutenzione dalle impostazioni dell'account
    const getMaintenanceActivityStatusColor = (statusId) => {
        if (!statusId || !accountData || !accountData.settings || !accountData.settings.maintenanceActivitiesStatus) return '#6c757d'; // Default: grigio
        
        const matchingStatus = accountData.settings.maintenanceActivitiesStatus.find(status => status._id === statusId);
        
        if (matchingStatus && matchingStatus.color) {
            return matchingStatus.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Form state - allineato con MaintenanceSchema
    const [formData, setFormData] = useState({
        code: '',
        notes: '',
        creationDate: '',
        customerId: '',
        customerLabel: '',
        systemId: '',
        systemLabel: '',
        systemAddressId: '',
        systemAddressLabel: '',
        systemTypes: [],
        actions: []
    });
    
    // Stato per la nuova azione (allineato con schema: title, notes)
    const [newAction, setNewAction] = useState({
        title: '',
        notes: ''
    });
    
    const [validation, setValidation] = useState({
        code: true,
        customerId: true
    });

    // Conferma cancellazione azioni collegate a commesse
    const [confirmData, setConfirmData] = useState(null); // { linkedActions, message }
    const [pendingData, setPendingData] = useState(null); // dati manutenzione in attesa di conferma
    
    // Fetch maintenance details if in edit mode
    useEffect(() => {
        console.log('[AddEditMaintenance] useEffect triggered - mode:', mode, 'selectedMaintenance:', selectedMaintenance);
        if (mode === 'edit' && selectedMaintenance?._id) {
            console.log('[AddEditMaintenance] Popolo formData con:', selectedMaintenance);
            setFormData({
                code: selectedMaintenance.code || '',
                notes: selectedMaintenance.notes || '',
                creationDate: selectedMaintenance.creationDate ? new Date(selectedMaintenance.creationDate).toISOString().split('T')[0] : '',
                customerId: selectedMaintenance.customerId || '',
                customerLabel: selectedMaintenance.customerLabel || '',
                systemId: selectedMaintenance.systemId || '',
                systemLabel: selectedMaintenance.systemLabel || '',
                systemAddressId: selectedMaintenance.systemAddressId || '',
                systemAddressLabel: selectedMaintenance.systemAddressLabel || '',
                systemTypes: selectedMaintenance.systemTypes || [],
                actions: selectedMaintenance.actions || []
            });
        }
    }, [mode, selectedMaintenance]);
    
    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Reset validation for this field
        if (name in validation) {
            setValidation(prev => ({
                ...prev,
                [name]: true
            }));
        }
    };
    
    // Validate form
    const validateForm = () => {
        const newValidation = {
            code: !!formData.code?.trim(),
            customerId: !!formData.customerId
        };
        
        setValidation(newValidation);
        return Object.values(newValidation).every(isValid => isValid);
    };
    
    // Non gestiamo lo stato della manutenzione, solo quello delle azioni
    
    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        // Prepara le azioni con gli stati corretti
        let updatedActions = [];
        
        if (formData.actions && formData.actions.length > 0) {
            // Trova lo stato iniziale per le attività di manutenzione
            const activityInitialStatus = accountData?.settings?.maintenanceActivitiesStatus?.find(status => 
                status.code === 'dapianificare' || status.code === 'DaPianificare' || status.isInitial === true
            );
            
            updatedActions = formData.actions.map(action => {
                // Se l'azione non ha già uno stato, imposta quello iniziale
                if (!action.statusId && activityInitialStatus) {
                    return {
                        ...action,
                        statusId: activityInitialStatus._id,
                        statusLabel: activityInitialStatus.label || 'Da pianificare'
                    };
                }
                return action;
            });
        }
        
        const maintenanceData = {
            ...formData,
            actions: updatedActions,
            accountId: accountData._id
        };
        
        if (mode === 'add') {
            dispatch(createMaintenance({
                maintenance: maintenanceData,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                }
            });
        } else {
            const dataToUpdate = {
                ...maintenanceData,
                _id: selectedMaintenance._id
            };
            setPendingData(dataToUpdate);
            doUpdate(dataToUpdate, false);
        }
    };

    // Esegue l'aggiornamento della manutenzione. Con force=false il backend puo'
    // rispondere richiedendo conferma se si stanno eliminando azioni collegate a commesse.
    const doUpdate = (maintenanceData, force) => {
        dispatch(updateMaintenance({
            maintenance: maintenanceData,
            token,
            force
        })).then((result) => {
            if (!result.error) {
                setConfirmData(null);
                setPendingData(null);
                dispatch(closeModal());
                dispatch(setHasToRefresh());
            } else if (result.payload && result.payload.requiresConfirmation) {
                // Il backend chiede conferma: mostro il modale con le commesse collegate
                setConfirmData(result.payload);
            }
        });
    };
    
    // Reset error states when component unmounts
    useEffect(() => {
        return () => {
            dispatch(resetCreateStatus());
            dispatch(resetUpdateStatus());
        };
    }, [dispatch]);
    
    // Funzione per aggiungere una nuova azione
    const handleAddAction = () => {
        if (!newAction.title.trim()) return;
        
        // Trova lo stato iniziale "da pianificare" per le attività
        const activityInitialStatus = accountData?.settings?.maintenanceActivitiesStatus?.find(status => 
            status.code === 'dapianificare' || status.code === 'DaPianificare' || status.isInitial === true
        );
        
        // Aggiungi la nuova azione con lo stato predefinito
        const actionToAdd = {
            ...newAction,
            id: Date.now().toString(), // ID temporaneo
            statusId: activityInitialStatus?._id || '',
            statusLabel: activityInitialStatus?.label || 'Da pianificare'
        };
        
        setFormData(prev => ({
            ...prev,
            actions: [...prev.actions, actionToAdd]
        }));
        
        // Reset del form
        setNewAction({
            title: '',
            notes: ''
        });
    };
    
    // Funzione per rimuovere un'azione
    const handleRemoveAction = (actionId) => {
        setFormData(prev => ({
            ...prev,
            actions: prev.actions.filter(action => (action.id || action._id) !== actionId)
        }));
    };
    
    // Funzione per gestire i cambiamenti nei campi della nuova azione
    const handleNewActionChange = (e) => {
        const { name, value } = e.target;
        setNewAction(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    // Mostra loading se in modalità edit e i dati non sono ancora caricati
    if (mode === 'edit' && (selectedMaintenanceLoading || !selectedMaintenance)) {
        return <LoadingSection />;
    }
    
    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">
                {mode === 'add' ? 'Aggiungi Nuova Manutenzione' : 'Modifica Manutenzione'}
            </h2>
            
            {(createLoading || updateLoading) && <LoadingSection isFixed />}
            
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Codice */}
                    <div>
                        <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                            Codice*
                        </label>
                        <input
                            type="text"
                            id="code"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                            className={`w-full p-2 border ${!validation.code ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            placeholder="Codice manutenzione"
                            readOnly={mode === 'edit'}
                        />
                        {!validation.code && (
                            <p className="text-red-500 text-sm mt-1">Il codice è obbligatorio</p>
                        )}
                    </div>
                    
                    {/* Data Creazione */}
                    <div>
                        <label htmlFor="creationDate" className="block text-sm font-medium text-gray-700 mb-1">
                            Data Creazione
                        </label>
                        <input
                            type="date"
                            id="creationDate"
                            name="creationDate"
                            value={formData.creationDate}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    
                    {/* Cliente */}
                    <div>
                        <label htmlFor="customerLabel" className="block text-sm font-medium text-gray-700 mb-1">
                            Cliente*
                        </label>
                        <input
                            type="text"
                            id="customerLabel"
                            name="customerLabel"
                            value={formData.customerLabel}
                            onChange={handleInputChange}
                            className={`w-full p-2 border ${!validation.customerId ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100`}
                            placeholder="Cliente"
                            readOnly
                        />
                        {!validation.customerId && (
                            <p className="text-red-500 text-sm mt-1">Il cliente è obbligatorio</p>
                        )}
                    </div>
                    
                    {/* Sistema/Impianto */}
                    <div>
                        <label htmlFor="systemLabel" className="block text-sm font-medium text-gray-700 mb-1">
                            Impianto
                        </label>
                        <input
                            type="text"
                            id="systemLabel"
                            name="systemLabel"
                            value={formData.systemLabel}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                            placeholder="Impianto"
                            readOnly
                        />
                    </div>
                    
                    {/* Indirizzo Impianto */}
                    <div className="col-span-2">
                        <label htmlFor="systemAddressLabel" className="block text-sm font-medium text-gray-700 mb-1">
                            Indirizzo Impianto
                        </label>
                        <input
                            type="text"
                            id="systemAddressLabel"
                            name="systemAddressLabel"
                            value={formData.systemAddressLabel}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                            placeholder="Indirizzo"
                            readOnly
                        />
                    </div>
                    
                    {/* Note */}
                    <div className="col-span-2">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                            Note
                        </label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            rows="3"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Note aggiuntive"
                        ></textarea>
                    </div>
                    
                    {/* Sezione Azioni Programmate */}
                    <div className="col-span-2 mt-4">
                        <h3 className="text-lg font-semibold mb-3">
                            <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                            Azioni Programmate
                        </h3>
                        
                        {/* Form per nuova azione */}
                        <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="md:col-span-2">
                                    <label htmlFor="actionTitle" className="block text-sm font-medium text-gray-700 mb-1">
                                        Titolo Azione*
                                    </label>
                                    <input
                                        type="text"
                                        id="actionTitle"
                                        name="title"
                                        value={newAction.title}
                                        onChange={handleNewActionChange}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Titolo azione"
                                    />
                                </div>
                                <div className="md:col-span-1 flex items-end">
                                    <button
                                        type="button"
                                        className="w-full p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onClick={handleAddAction}
                                        disabled={!newAction.title.trim()}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                        Aggiungi
                                    </button>
                                </div>
                                <div className="md:col-span-3">
                                    <label htmlFor="actionNotes" className="block text-sm font-medium text-gray-700 mb-1">
                                        Note
                                    </label>
                                    <textarea
                                        id="actionNotes"
                                        name="notes"
                                        value={newAction.notes}
                                        onChange={handleNewActionChange}
                                        rows="2"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Note dell'azione"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        
                        {/* Anteprima delle azioni programmate */}
                        <div className="mb-3">
                            <h4 className="text-md font-semibold mb-2">Azioni Aggiunte:</h4>
                            {formData.actions.length > 0 ? (
                                <div className="space-y-2">
                                    {formData.actions.map((action, index) => (
                                        <div key={action.id || action._id || index} className="p-3 border border-gray-200 rounded-md flex justify-between items-center">
                                            <div>
                                                <div className="font-semibold">{action.title}</div>
                                                {action.notes && (
                                                    <div className="text-sm text-gray-600 mt-1">{action.notes}</div>
                                                )}
                                                {action.statusId && (
                                                    <div className="mt-2">
                                                        <span 
                                                            className="badge px-2 py-1 rounded" 
                                                            style={{ 
                                                                backgroundColor: getMaintenanceActivityStatusColor(action.statusId),
                                                                fontSize: '0.75rem',
                                                                color: '#fff'
                                                            }}
                                                        >
                                                            {action.statusLabel}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="text-red-500 hover:text-red-700"
                                                onClick={() => handleRemoveAction(action.id || action._id)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-3 border border-gray-200 rounded-md text-center text-gray-500">
                                    Nessuna azione aggiunta
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Error message */}
                {(createError || updateError) && (
                    <div className="p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{createError || updateError}</p>
                    </div>
                )}
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>
                    
                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                {mode === 'add' ? 'Crea Manutenzione' : 'Aggiorna Manutenzione'}
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Modale di conferma: azioni collegate a commesse in fase di cancellazione */}
            {confirmData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <FontAwesomeIcon icon={faTimes} className="text-amber-500 text-xl mt-1" />
                            <div>
                                <h3 className="text-lg font-bold mb-1">Attenzione: azioni collegate a commesse</h3>
                                <p className="text-sm text-gray-600">
                                    {confirmData.message || 'Alcune azioni che stai eliminando sono collegate a delle commesse. Confermando, il collegamento andrà perso.'}
                                </p>
                            </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3 mb-4 space-y-3">
                            {(confirmData.linkedActions || []).map((action) => (
                                <div key={action.actionId} className="text-sm">
                                    <p className="font-semibold">
                                        {action.actionTitle || 'Azione'}{action.actionPeriod ? ` (${action.actionPeriod})` : ''}
                                    </p>
                                    <p className="text-gray-600">
                                        Commesse collegate: {(action.jobs || []).map(j => j.code).join(', ')}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                className="secondary"
                                onClick={() => setConfirmData(null)}
                                disabled={updateLoading}
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                className="primary"
                                onClick={() => {
                                    setConfirmData(null);
                                    if (pendingData) doUpdate(pendingData, true);
                                }}
                                disabled={updateLoading}
                            >
                                Elimina comunque
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
